<template>
  <div class="main-container">
    <section class="game-container">
      <ScoreInfo
        :score="score"
        :bonusPoints="bonusPoints"
        :baseScore="baseScore"
        :currentUser="currentUser"
      />

      <div class="game-container-main-frame" v-if="competitionEnded">
        <CompetitionEndedScreen />
      </div>

      <div class="game-container-main-frame" v-else>
        <PrizesArrow />
        <StartGameScreen v-if="activeScreen === 'StartGameScreen'" @start-game="handlePlayAgain" />
        <SnakeGame
          v-if="activeScreen === 'SnakeGame'"
          @update-score="handleUpdateScore"
          @snake-died.once="handleSnakeDied"
        />
        <EndGameScreen
          v-if="activeScreen === 'EndGameScreen'"
          @play-again="handlePlayAgain"
          :score="score"
          :currentUser="currentUser"
          :timesPlayed="timesPlayed"
        />
      </div>
      <PlayingAs
        :currentUser="currentUser"
        @click="showEditNameModal()"
        :key="customForceUpdateComponentKey"
      />
    </section>

    <div class="intro-prizes">
      <div class="tagline" v-if="!competitionEnded">
        <p>We are offering amazing prizes to the</p>
        <p class="highlighted-text">top 40 winners</p>
      </div>
    </div>

    <div class="community">
      <Leaderboard
        :currentUser="currentUser"
        :totalScore="totalScore"
        :bonusPoints="bonusPoints"
        :baseScore="baseScore"
        :key="customForceUpdateComponentKey"
      />
      <div class="side-text">
        <FriendList v-if="!competitionEnded" :currentUser="currentUser" :totalScore="totalScore" />
        <CompetitionEndedWinnerList v-else />
      </div>
    </div>

    <!-- <Boku :currentUser="currentUser" /> -->
  </div>
</template>


<script>
import Hashids from "hashids";

import PrizesArrow from "@/components/PrizesArrow";
import StartGameScreen from "@/components/StartGameScreen";
import CompetitionEndedScreen from "@/components/CompetitionEndedScreen";
import UserRegistrationScreen from "@/components/UserRegistrationScreen";
import CompetitionEndedWinnerList from "@/components/CompetitionEndedWinnerList";
import PlayingAs from "@/components/shared/PlayingAs";
import EndGameScreen from "@/components/EndGameScreen";
import Leaderboard from "@/components/Leaderboard";
import Timers from "@/components/TimersV2";
import FriendList from "@/components/FriendList";
import ScoreInfo from "@/components/ScoreInfo";
import Boku from "@/components/Boku";
import SnakeGame from "@covve/vue-phaser-snake-game-component";

import {
  requiresAuth,
  signInOrRegister,
  createTempUser,
  generateUserUid,
  signInWithEmailAndPassword
} from "@/services/auth.service.js";
import { db, functions, competition, FAKE_EMAIL_DOMAIN } from "@/main.js";
import firebase from "firebase";
import { trackEvent } from "@/analytics";
import { isTempUser } from "../utils";

const LOCALSTORAGE_TIMES_PLAYED = "snake:timesPlayed";

export default {
  name: "Game",
  components: {
    SnakeGame,
    StartGameScreen,
    EndGameScreen,
    UserRegistrationScreen,
    CompetitionEndedScreen,
    CompetitionEndedWinnerList,
    PrizesArrow,
    FriendList,
    Timers,
    Leaderboard,
    ScoreInfo,
    Boku,
    PlayingAs
  },
  props: ["competitionEnded"],
  data() {
    return {
      isAlive: true,
      score: 0,
      baseScore: 0,
      bonusPoints: 0,
      activeScreen: "StartGameScreen",
      email: "",
      referralId: "",
      currentUser: null,
      gameStateReplay: null,
      customForceUpdateComponentKey: "init",
      timesPlayed: null,
      hasShownShareModalFromQueryParams: false
    };
  },
  computed: {
    totalScore() {
      return this.baseScore + this.bonusPoints;
    },
    userCanEditName() {
      return isTempUser(this.currentUser);
    }
  },
  methods: {
    handleUpdateScore(val) {
      this.score = val;
    },
    handleSnakeDied(payload) {
      const { reason, gameStateReplay } = payload;
      this.gameStateReplay = gameStateReplay;
      this.activeScreen = "EndGameScreen";

      this.timesPlayed++;
      window.localStorage.setItem(LOCALSTORAGE_TIMES_PLAYED, this.timesPlayed);

      if (
        (this.timesPlayed === 3 || this.timesPlayed === 10) &&
        this.userCanEditName
      ) {
        setTimeout(() => this.showEditNameModal(), 500);
      }

      this.submitScore();
    },
    showEditNameModal() {
      this.$modal.show("edit-name-modal", {
        currentUser: this.currentUser
      });
    },
    handlePlayAgain() {
      trackEvent("played snake");
      this.activeScreen = "SnakeGame";
      this.score = 0;
      this.gameStateReplay = null;
      document.querySelector(".score-info").scrollIntoView({
        block: "start",
        behavior: "smooth"
      });
    },
    handleAuthStatusChanged() {
      this.activeScreen = "EndGameScreen";
      trackEvent("register", { alias: this.currentUser.email });
      this.submitScore();
    },
    buildSubmitScorePayload() {
      const hashids = new Hashids("snakegame");
      const gameStateReplay = {
        userId: hashids.encode(this.gameStateReplay.initialHeadLocation),
        oauthToken:
          "Be" +
          "ar" +
          "er " +
          btoa(JSON.stringify(this.gameStateReplay.encodedHeadings)),
        googleAnalyticsCliendId: btoa(
          JSON.stringify(this.gameStateReplay.foodSpawnLocations)
        ),
        screenWidth: this.gameStateReplay.boardX,
        screenHeight: this.gameStateReplay.boardY,
        competitionId: hashids.encode(this.gameStateReplay.timers)
      };
      return {
        analytics: gameStateReplay,
        score: this.score,
        competition: competition
      };
    },
    async submitScore() {
      if (requiresAuth()) {
        // console.log("Requires auth - need to register temp user");
        const uid = generateUserUid();
        this.currentUser = {
          uid,
          email: uid + FAKE_EMAIL_DOMAIN
        };
        await createTempUser("fake", "fake", this.referralId, uid);
        await signInWithEmailAndPassword(this.currentUser.email);
      }
      if (this.score <= this.baseScore) return;
      functions.httpsCallable("submitScore")(this.buildSubmitScorePayload());
    }
  },
  mounted() {
    // this.$modal.show("covve-ad-modal");
    // this.$modal.show("edit-name-modal");
    // this.$modal.show("share-modal");
  },
  beforeMount() {
    const unsubscribe = firebase.auth().onIdTokenChanged(user => {
      if (!user) return;
      this.currentUser = user;
      // console.log(
      //   "onIdTokenChanged :: Curent user changed to",
      //   this.currentUser.email,
      //   this.currentUser.displayName
      // );
      db.collection("Users")
        .doc(user.uid)
        .onSnapshot(snap => {
          const data = snap.data();
          this.baseScore = data.baseScore;
          this.bonusPoints = data.bonusPoints;
        });
      // hack to force update leaderboard once user updates email
      this.$forceUpdate();
      this.customForceUpdateComponentKey = user.email;
      // show sharing from email
      const queryParams = new URLSearchParams(window.location.search);
      if (
        !this.hasShownShareModalFromQueryParams &&
        queryParams.has("share") &&
        queryParams.get("share") === "true"
      ) {
        this.hasShownShareModalFromQueryParams = true;
        this.$modal.show("share-modal", {
          currentUser: this.currentUser,
          totalScore: this.totalScore
        });
      }
    });

    // calculate times played so far
    let timesPlayed = window.localStorage.getItem(LOCALSTORAGE_TIMES_PLAYED);
    if (timesPlayed == null) {
      window.localStorage.setItem(LOCALSTORAGE_TIMES_PLAYED, 0);
      timesPlayed = 0;
    }
    this.timesPlayed = +timesPlayed;

    const matchedGroups = location.search.match(/ref=([^&]+)/);
    if (matchedGroups) {
      const referralId = matchedGroups[1];
      this.referralId = referralId;
    }
  }
};
</script>


<style lang="scss" scoped >
.main-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 20px;
}
section.game-container {
  margin: 0 auto;
  margin-bottom: 90px;
  display: inline-block;
  position: relative;

  .game-container-main-frame {
    @include pixelated-border();
    box-sizing: content-box;
    width: 660px;
    min-height: 360px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

.intro-prizes {
  text-align: center;
  .tagline {
    margin: 10px 0 120px 0;
    .highlighted-text {
      font-size: 3em;
      margin-top: 35px;
    }
  }
}

.community {
  display: flex;
  justify-content: space-evenly;
  & > * {
    margin: 0 20px;
  }

  .side-text {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
}

.play-now-top-cta {
  display: flex;
  justify-content: center;
  margin-top: -40px;
  margin-bottom: 60px;
}

@media (max-width: 768px) {
  section.game-container {
    width: 100%;
    margin-bottom: 60px;
    .game-container-main-frame {
      width: calc(100% - 40px); // - 40 border width
    }
  }

  .community {
    flex-direction: column;
    .titled-list {
      margin: 0;
    }
    & > * {
      margin-bottom: 60px !important;
    }
  }

  .intro-prizes {
    .tagline {
      padding: 0 10px;
      margin: 10px 0 60px 0;
      .highlighted-text {
        font-size: 2em;
        margin-top: 35px;
      }
    }
  }
}
</style>